import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Camera, Upload, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { roadStatuses } from '../mockDataVegr';
import { useToast } from '../hooks/use-toast';
import { submitReport } from '../services/apiService';

const VegrReportPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    roadName: '',
    location: '',
    status: 'damaged',
    description: '',
    reportedBy: '',
    photo: null
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, photo: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Parse coordinates if they're from GPS
      let coords = [];
      if (formData.location.includes(',')) {
        const [lat, lon] = formData.location.split(',').map(s => parseFloat(s.trim()));
        if (!isNaN(lat) && !isNaN(lon)) {
          coords = [lat, lon];
        }
      }
      
      // If no valid coordinates, use a default or estimate from district
      if (coords.length === 0) {
        // Default to center of Nagaland if no GPS
        coords = [26.1584, 94.5624];
      }

      const reportData = {
        roadName: formData.roadName,
        location: formData.location,
        coordinates: coords,
        status: formData.status,
        severity: formData.status === 'blocked' ? 'high' : formData.status === 'damaged' ? 'medium' : 'info',
        description: formData.description,
        reportedBy: formData.reportedBy || 'Anonymous',
        district: formData.location.split(',')[0] || 'Unknown',
        state: 'Nagaland', // Default, can be enhanced with geocoding
        photo: photoPreview // Base64 image
      };

      const result = await submitReport(reportData);

      toast({
        title: "Report Submitted Successfully!",
        description: `Your report has been saved with ID: ${result.id.substring(0, 8)}...`,
      });

      setIsSubmitting(false);
      
      // Navigate to map after short delay
      setTimeout(() => {
        navigate('/vegr');
      }, 2000);
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: "Submission Failed",
        description: "Unable to submit report. Please try again.",
        variant: "destructive"
      });
      setIsSubmitting(false);
    }
  };

  const useCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setFormData(prev => ({
            ...prev,
            location: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
          }));
          toast({
            title: "Location Captured",
            description: "Your current location has been added to the report.",
          });
        },
        (error) => {
          toast({
            title: "Location Error",
            description: "Unable to get your location. Please enter it manually.",
            variant: "destructive"
          });
        }
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#E8DED0] py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-[#2C4A5E] rounded-full flex items-center justify-center">
              <MapPin className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Report Road Condition</h1>
          <p className="text-gray-600">Help keep Nagaland's roads safe by reporting current conditions</p>
        </div>

        {/* Form */}
        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-[#2C4A5E] to-[#1e3544] text-white">
            <CardTitle>Road Condition Report Form</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Road Name */}
              <div>
                <Label htmlFor="roadName">Road/Highway Name *</Label>
                <Input
                  id="roadName"
                  name="roadName"
                  placeholder="e.g., NH 29, Dimapur-Kohima Road"
                  value={formData.roadName}
                  onChange={handleInputChange}
                  required
                  className="mt-1"
                />
              </div>

              {/* Location */}
              <div>
                <Label htmlFor="location">Specific Location *</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="location"
                    name="location"
                    placeholder="e.g., Near Medziphema Junction, 15km from Kohima"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={useCurrentLocation}
                    className="whitespace-nowrap"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Use GPS
                  </Button>
                </div>
              </div>

              {/* Status */}
              <div>
                <Label htmlFor="status">Road Status *</Label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2C4A5E]"
                >
                  {roadStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label} - {status.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Describe the road condition in detail (e.g., landslide blocking both lanes, large pothole on left side, etc.)"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="mt-1"
                />
              </div>

              {/* Reporter Name */}
              <div>
                <Label htmlFor="reportedBy">Your Name (Optional)</Label>
                <Input
                  id="reportedBy"
                  name="reportedBy"
                  placeholder="Anonymous if left blank"
                  value={formData.reportedBy}
                  onChange={handleInputChange}
                  className="mt-1"
                />
              </div>

              {/* Photo Upload */}
              <div>
                <Label htmlFor="photo">Upload Photo (Optional but recommended)</Label>
                <div className="mt-1">
                  <label
                    htmlFor="photo"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="h-full object-cover rounded-lg"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Camera className="w-10 h-10 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                      </div>
                    )}
                    <input
                      id="photo"
                      name="photo"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-[#4A7C59] hover:bg-[#3d6949] text-white py-6 text-lg"
                >
                  {isSubmitting ? (
                    <>
                      <Upload className="animate-spin mr-2 w-5 h-5" />
                      Submitting Report...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 w-5 h-5" />
                      Submit Report
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/vegr')}
                  className="px-8 py-6 text-lg"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="mt-8 bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-2">📱 SMS Reporting (Coming Soon)</h3>
            <p className="text-sm text-gray-700 mb-2">
              Soon you'll be able to report road conditions via SMS:
            </p>
            <p className="text-sm font-mono bg-white p-2 rounded">
              VEGR [Location] [Status] [Description]
            </p>
            <p className="text-xs text-gray-600 mt-2">
              Example: VEGR Kohima-Dimapur BLOCKED Landslide near Medziphema
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VegrReportPage;